import {
    select as D3select,
    local as D3local,
    scaleTime as D3scaleTime,
    scaleLinear as D3scaleLinear,
    scaleLog as D3scaleLog,
    max as D3max,
    min as D3min,
    extent as D3extent,
    line as D3line,
    area as D3area,
    curveMonotoneX as D3curveMonotoneX,
    easeLinear as D3easeLinear,
    dispatch as D3dispatch,
} from 'd3';
// a single sparkline chart
// expects: { {key, title, subtitle, value}, timeline }
const D3Sparkline = function () {
    // default properties
    let localY = D3local();
    let localKey = D3local();
    let settings = {};
    let color = () => '';
    let x = D3scaleTime();
    let xval = d => new Date(d.timestamp);
    let theme;
    let chartHeight;

    // set to true when it's not part of a group and you want it to handle 'barClick'
    let standalone = false;

    const dispatch = D3dispatch('labelClick', 'barClick', 'click');

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            if (!data.metric) return;
            const height = chartHeight ? chartHeight(i) : settings.chartHeight;
            // need to be defensive
            const yf = function (d) {
                let ret = 0;
                if (!data.metric.value) {
                    ret = data.metric.nilValue;
                } else if (data.metric.value(d) === 0 && data.metric.nilValue) {
                    ret = data.metric.nilValue;
                } else if (data.metric.value(d)) {
                    ret = data.metric.value(d);
                }
                if (settings.scale !== 'linear') ret = D3max([ret, 1e-4]);
                return ret;
            };

            const extent = D3extent(data.timeline, yf);
            if (data.metric.minY !== undefined) { extent[0] = data.metric.minY }
            if (data.metric.maxY !== undefined) { extent[1] = data.metric.maxY }
            if (settings.scale !== 'linear') extent[0] = D3max([extent[0], 1e-4]);
            if (settings.scale !== 'linear') extent[1] = D3max([extent[1], 1e-4]);
            if (data.metric.trendline) extent[0] = D3min([extent[0], data.metric.trendline]);
            if (data.metric.trendline) extent[1] = D3max([extent[1], data.metric.trendline]);

            const s = !settings.scale || settings.scale === 'linear' ? D3scaleLinear : D3scaleLog;
            const ty = s()
                .range([height, 10])
                .domain(extent);

            // sanitize
            if (!ty.domain()[0]) ty.domain([0, ty.domain()[1]]);
            if (!ty.domain()[1]) ty.domain([ty.domain()[0], 0]);

            localKey.set(this, data.metric);
            localY.set(this, ty);

            // container for stuff that's only added once, e.g., titles and tooltips
            const g = D3select(this).selectAll('.timeline').data([data.metric]);
            const genter = g.enter()
                .append('g')
                .attr('class', 'timeline');

            const gmerge = genter.merge(g);

            // clip-path aded once on enter
            genter.append('text')
                .attr('text-anchor', 'start')
                .attr('y', height)
                .attr('class', 'title')
                .style('pointer-events', 'none')
                .merge(genter)
                .text(settings.labelWidth ? data.metric.title : '');

            if (data.metric.type === 'bar') {
                // parent container to ensure bars are above tooltip
                const barsg = gmerge.selectAll('.barsg').data(['once']);
                const barsgenter = barsg.enter()
                    .append('g')
                    .attr('class', 'barsg');

                const barsgmerge = barsgenter.merge(barsg);

                // create a barchart
                // enter + update
                const bjoin = barsgmerge.selectAll('.bar')
                    .data(data.timeline);

                const center = bjoin.enter().append('rect')
                    .attr('class', 'bar cursor');

                center.merge(bjoin)
                    .attr('x', function (d) { return x(xval(d)) })
                    .attr('y', function (d) {
                        return ty(yf(d));
                    })
                    .attr('width', settings.barWidth)
                    .attr('height', function (d) {
                        if (data.metric.value(d) === 0 && data.metric.nilValue) {
                            return height - ty(data.metric.nilValue);
                        }
                        return data.metric.value(d) ? height -
                            ty(data.metric.value(d)) : 0;
                    })
                    .attr('fill', !data.metric.bad && color ? color(data.metric.key) : '')
                    .classed('bad', data.metric.bad)
                    .classed('good', data.metric.good)
                    .on('click', (e, d) => {
                        dispatch.call('barClick', my, d);
                        dispatch.call('click', my, d);
                        e.stopPropagation();
                    })
                    .attr('clip-path', `url(#clip_path_${data.key})`)
                    .style('pointer-events', standalone ? 'all' : 'none')
                    .style('pointer', 'cursor');
                // old data rolls off the chart
                bjoin.exit().remove();
            } else {
                // default is a line chart
                // enter + update
                const line = D3line()
                    .curve(D3curveMonotoneX)
                    .defined(function (d) { return data.metric.value && data.metric.value(d) !== undefined })
                    .x(function (d) { return x(xval(d)) })
                    .y(function (d) { return ty(yf(d)) });

                const area = D3area()
                    .curve(line.curve())
                    .defined(line.defined())
                    .x(line.x())
                    .y1(line.y())
                    .y0(height);

                const ajoin = gmerge.selectAll('.linechartarea')
                    .data([data.timeline]);

                ajoin.enter()
                    .append('path')
                    .attr('class', 'linechartarea')
                    .merge(ajoin)
                    .on('click', d => {
                        dispatch.call('click', my, d);
                    })
                    .transition()
                    .ease(D3easeLinear)
                    .duration(200)
                    .attr('d', area)
                    .attr('clip-path', `url(#clip_path_${data.safekey})`)
                    .attr('fill', function (d) {
                        return color(data.metric.key) || D3select(this).style('color');
                    });

                const ljoin = gmerge.selectAll('.linechart')
                    .data([data.timeline]);

                ljoin.enter()
                    .append('path')
                    .attr('class', 'linechart')
                    .merge(ljoin)
                    .on('click', (e, d) => {
                        console.log('line clicked', d);
                        dispatch.call('click', my, d);
                        e.stopPropagation();
                    })
                    .transition()
                    .ease(D3easeLinear)
                    .duration(200)
                    .attr('d', line)
                    .attr('clip-path', `url(#clip_path_${data.safekey})`)
                    .attr('stroke', function (d) {
                        return color(data.metric.key) || D3select(this).style('color');
                    });
            }

            // trendlines enter + update
            const tline = D3line().curve(D3curveMonotoneX)
                .x(function (d) { return x(xval(d)) })
                .y(function (d) { return ty(data.metric.trendline) });
            const trendlines = [];

            if (data.metric.trendline) {
                trendlines.push(data.timeline);
            }

            const ljoin = gmerge.selectAll('.trendline')
                .data(trendlines);

            ljoin.enter()
                .append('path')
                .attr('class', 'trendline')
                .merge(ljoin)
                .transition()
                .ease(D3easeLinear)
                .duration(200)
                .attr('d', tline)
                .attr('clip-path', `url(#clip_path_${data.safekey})`);

            ljoin.exit().remove();

            // title
            genter.append('text')
                .attr('text-anchor', 'start')
                .attr('y', height)
                .attr('class', 'title')
                .style('pointer-events', 'none')
                .merge(genter)
                .text(settings.labelWidth ? data.metric.title : '');

            // subtitle
            genter.append('text')
                .attr('text-anchor', 'end')
                .attr('x', settings.labelWidth - 5)
                .attr('y', height)
                .attr('class', 'subtitle')
                .merge(genter)
                .text(data.metric.subtitle)
                .on('click', (e, d) => {
                    dispatch.call('labelClick', my, data.metric.key);
                    e.stopPropagation();
                });

            // fancy tooltips
            genter.append('g')
                .attr('class', 'tip tip-container')
                .style('pointer-events', 'none');

            genter.selectAll('.tip-container')
                .append('circle')
                .attr('r', 3)
                .attr('class', 'tip-circle')
                .style('fill', color(data.metric.key) || theme.palette.background.default)
                .style('opacity', '0')
                .style('pointer-events', 'none');

            genter.selectAll('.tip-container')
                .append('rect')
                .attr('class', 'tip-rect')
                .attr('height', 17)
                .attr('x', 5)
                .attr('y', height / 2 - 12)
                .attr('rx', 2)
                .attr('ry', 2)
                .style('fill', theme.palette.background.default)
                .style('stroke', theme.palette.info.dark)
                .style('pointer-events', 'none');

            genter.selectAll('.tip-container')
                .append('text')
                .attr('y', height / 2 + 5)
                .attr('class', 'tip-text tooltip')
                .style('pointer-events', 'none')
                .style('white-space', 'nowrap')
                .style('font-weight', 'bold');
        });
    }

    my.color = function (v) {
        if (!arguments.length) return color;
        color = v;
        return my;
    };

    my.x = function (v) {
        if (!arguments.length) return x;
        x = v;
        return my;
    };

    my.xval = function (v) {
        if (!arguments.length) return xval;
        xval = v;
        return my;
    };

    my.localY = function (v) {
        if (!arguments.length) return localY;
        localY = v;
        return my;
    };

    my.localKey = function (v) {
        if (!arguments.length) return localKey;
        localKey = v;
        return my;
    };

    my.settings = function (v) {
        if (!arguments.length) return settings;
        settings = v;
        return my;
    };

    my.chartHeight = function (v) {
        if (!arguments.length) return settings;
        chartHeight = v;
        return my;
    };

    my.standalone = function (v) {
        if (!arguments.length) return standalone;
        standalone = v;
        return my;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    return my;
};

export default D3Sparkline;
